import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {ReturnType<typeof setTimeout> | null} */
  fabelleCartDrawerUpdateTimer = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line, previousQuantity } = event.detail;

    // Cart items require a line number
    if (!line) return;

    /* Minus at qty 1 → 0: remove line, pink toast, no trash modal. */
    if (quantity === 0) {
      return this.executeLineItemRemove(line, { showRemovalToast: true });
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      previousQuantity,
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Trash control: confirm in modal, then remove (no "removed" toast; modal is the UX).
   * @param {number} line - The line item index.
   */
  onTrashRemoveLineItem(line) {
    if (typeof window.fabelleConfirmCartLineRemove === 'function') {
      const self = this;
      window.fabelleConfirmCartLineRemove(this, line, function () {
        self.executeLineItemRemove(line, { showRemovalToast: true });
      });
      return;
    }
    this.executeLineItemRemove(line, { showRemovalToast: true });
  }

  /**
   * Removes the line (optimistic UI + /cart/change).
   * @param {number} line - The line item index.
   * @param {Object} [options]
   * @param {boolean} [options.showRemovalToast] - Pink toast for minus-to-zero; false for trash after confirm
   */
  executeLineItemRemove(line, options = {}) {
    const { showRemovalToast = false } = options;
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      fabelleProductRemovedToast: showRemovalToast,
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      if (this.isDrawer) {
        startViewTransition(() => {
          this.replaceChildren(clone);
        }, ['empty-cart-drawer']);
      } else {
        startViewTransition(() => {
          this.replaceChildren(clone);
        }, ['empty-cart-page']);
      }

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  /** Pink top toasts for cart *page* only; cart drawer uses in-drawer strip (see #fabelleAfterDrawerCartMorph). */
  #fabelleCartQuantityToasts(config, parsedResponseText) {
    if (this.isDrawer) {
      return;
    }
    if (typeof window.fabelleShowMobileCartToast !== 'function') return;
    const line = config.line;
    if (config.action === 'clear' && config.fabelleProductRemovedToast) {
      window.fabelleShowMobileCartToast('Product has been removed from cart');
      return;
    }
    if (
      config.action === 'change' &&
      typeof config.previousQuantity === 'number' &&
      !Number.isNaN(config.previousQuantity)
    ) {
      const items = parsedResponseText.items;
      const item = Array.isArray(items) ? items[line - 1] : null;
      const n = item != null ? Number(item.quantity) : config.quantity;
      const p = config.previousQuantity;
      if (n < p) {
        window.fabelleShowMobileCartToast('Item quantity decreased');
      } else if (n > p) {
        window.fabelleShowMobileCartToast('Item quantity increased');
      }
    }
  }

  /**
   * Full-width "Cart Updated" in drawer: after morph so the node survives section refresh.
   */
  #fabelleAfterDrawerCartMorph(config) {
    if (!this.isDrawer) {
      return;
    }
    const isPlusMinus =
      config.action === 'change' &&
      typeof config.previousQuantity === 'number' &&
      !Number.isNaN(config.previousQuantity);
    const isMinusToZero = config.action === 'clear' && config.fabelleProductRemovedToast;
    if (!isPlusMinus && !isMinusToZero) {
      return;
    }

    const content = this.querySelector('.cart-drawer__content');
    if (!content) {
      return;
    }

    let bar = content.querySelector('#fabelle-cart-drawer-updated-bar');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'fabelle-cart-drawer-updated-bar';
      bar.className = 'fabelle-cart-drawer-updated';
      bar.setAttribute('role', 'status');
      bar.setAttribute('aria-live', 'polite');
    }
    bar.textContent = isMinusToZero ? 'Product has been removed from cart' : 'Cart Updated';
    if (bar.parentElement) {
      bar.remove();
    }
    const pincode = content.querySelector('.cart-drawer__strip--pincode');
    const items = content.querySelector('.cart-drawer__items');
    if (pincode) {
      pincode.insertAdjacentElement('beforebegin', bar);
    } else if (items) {
      items.insertAdjacentElement('beforebegin', bar);
    } else {
      content.prepend(bar);
    }

    if (this.fabelleCartDrawerUpdateTimer) {
      clearTimeout(this.fabelleCartDrawerUpdateTimer);
    }
    requestAnimationFrame(() => {
      bar.classList.add('fabelle-cart-drawer-updated--show');
    });
    this.fabelleCartDrawerUpdateTimer = setTimeout(() => {
      bar.classList.remove('fabelle-cart-drawer-updated--show');
    }, 4000);
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        this.#fabelleCartQuantityToasts(config, parsedResponseText);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Total units: sum line quantities from JSON (matches visible qty rows; item_count can lag in edge cases).
        let newCartItemCount = 0;
        if (Array.isArray(parsedResponseText.items)) {
          newCartItemCount = parsedResponseText.items.reduce(
            (sum, line) => sum + (Number(line.quantity) || 0),
            0
          );
        } else if (typeof parsedResponseText.item_count === 'number') {
          newCartItemCount = parsedResponseText.item_count;
        } else {
          const hiddenCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          newCartItemCount = hiddenCount ? parseInt(hiddenCount, 10) || 0 : 0;
        }

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        await morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
          this.isDrawer ? 'hydration' : 'full'
        );

        this.#updateCartQuantitySelectorButtonStates();

        /* Ensure items scroll is never lost after morph */
        if (this.isDrawer) {
          const itemsEl = this.querySelector('.cart-drawer__items');
          if (itemsEl instanceof HTMLElement) itemsEl.style.overflowY = 'auto';
        }

        this.#fabelleAfterDrawerCartMorph(config);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
      });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, this.isDrawer ? 'hydration' : 'full');

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
      });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}